import React, { FC, useState } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Chip,
    Grid,
    Card,
    CardContent,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import { ExpandMore } from "@mui/icons-material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoIcon from "@mui/icons-material/Info";
import { StoryNavigationNode } from "common/build/api-parameters/story";
import { isAutoContinueDecisionPoint } from "common/build/utils/branchedStoryUtils";
import type { MappedStoryQuestionType } from "../../../../models/modelTypes";

const useStyles = makeStyles(() =>
    createStyles({
        accordion: {
            marginBottom: "16px!important",
            border: "1px solid #e0e0e0",
        },
        accordionHeader: {
            minHeight: "64px",
        },
        partNumber: {
            backgroundColor: "#121738",
            color: "#ffffff",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            fontWeight: "bold",
            marginRight: "12px",
        },
        contentBox: {
            backgroundColor: "#f9f9f9",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "16px",
        },
        sectionTitle: {
            marginTop: "24px",
            marginBottom: "16px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "8px",
        },
        decisionPointCard: {
            marginBottom: "12px",
            backgroundColor: "#EBF7F3",
            border: "1px solid #1FAF8C",
        },
        branchOption: {
            backgroundColor: "#ffffff",
            borderRadius: "4px",
            padding: "12px",
            marginBottom: "8px",
            border: "1px solid #e0e0e0",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
                backgroundColor: "#f5f5f5",
                borderColor: "#1FAF8C",
                boxShadow: "0 2px 4px rgba(31, 175, 140, 0.2)",
            },
        },
        questionCard: {
            marginBottom: "12px",
            backgroundColor: "#f0f2f8",
            border: "1px solid #ddd",
        },
        chip: {
            marginRight: "8px",
        },
        branchLabel: {
            backgroundColor: "#1FAF8C",
            color: "#ffffff",
            borderRadius: "4px",
            padding: "4px 8px",
            fontSize: "12px",
            fontWeight: "bold",
            minWidth: "24px",
            textAlign: "center",
        },
        legendAccordion: {
            marginBottom: "24px!important",
            border: "2px solid #1FAF8C",
            backgroundColor: "#F0FDF9",
        },
        legendItem: {
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "12px",
        },
        legendIcon: {
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "4px",
        },
        storyPartIcon: {
            backgroundColor: "#FEF3C7",
            border: "2px solid #F59E0B",
        },
        decisionPointIcon: {
            backgroundColor: "#DBEAFE",
            border: "2px solid #3B82F6",
        },
        questionIcon: {
            backgroundColor: "#F3E8FF",
            border: "2px solid #A855F7",
        },
    }),
);

interface BranchedStoryAccordionViewProps {
    parts: StoryNavigationNode[];
}

export const BranchedStoryAccordionView: FC<BranchedStoryAccordionViewProps> = ({ parts }) => {
    const classes = useStyles();
    const [expandedPart, setExpandedPart] = useState<number | null>(null);

    const handleAccordionChange = (partNumber: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedPart(isExpanded ? partNumber : null);
    };

    const handleBranchClick = (targetPartNumber: number) => {
        // Expand the target accordion
        setExpandedPart(targetPartNumber);

        // Scroll to the target part after a short delay to allow accordion to expand
        setTimeout(() => {
            const element = document.getElementById(`story-part-${targetPartNumber}`);
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }, 100);
    };

    return (
        <Box>
            {/* Legend Section */}
            <Accordion className={classes.legendAccordion} defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <InfoIcon style={{ color: "#1FAF8C" }} />
                        <Typography variant="h6" style={{ fontWeight: "bold", color: "#1FAF8C" }}>
                            Story Structure Legend
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={3}>
                        {/* Story Parts Explanation */}
                        <Grid item xs={12} md={4}>
                            <Box className={classes.legendItem}>
                                <Box className={`${classes.legendIcon} ${classes.storyPartIcon}`}>
                                    <Typography variant="h6" style={{ fontWeight: "bold" }}>
                                        1
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                                        Story Part
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Main story content sections
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Decision Points Explanation */}
                        <Grid item xs={12} md={4}>
                            <Box className={classes.legendItem}>
                                <Box className={`${classes.legendIcon} ${classes.decisionPointIcon}`}>
                                    <AccountTreeIcon style={{ color: "#3B82F6" }} />
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                                        Decision Points
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Choice moments where the story branches
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Discussion Questions Explanation */}
                        <Grid item xs={12} md={4}>
                            <Box className={classes.legendItem}>
                                <Box className={`${classes.legendIcon} ${classes.questionIcon}`}>
                                    <QuestionAnswerIcon style={{ color: "#A855F7" }} />
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                                        Discussion Questions
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Questions for each story part
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>

                        {/* How to Navigate */}
                        <Grid item xs={12}>
                            <Box
                                p={2}
                                style={{
                                    backgroundColor: "#ffffff",
                                    borderRadius: "8px",
                                    border: "1px solid #e0e0e0",
                                }}
                            >
                                <Typography variant="subtitle2" style={{ fontWeight: "bold", marginBottom: "8px" }}>
                                    How to Navigate:
                                </Typography>
                                <Typography variant="body2" style={{ marginBottom: "4px" }}>
                                    • Click on any story part number to expand/collapse its content
                                </Typography>
                                <Typography variant="body2" style={{ marginBottom: "4px" }}>
                                    • Click on decision point branch cards (e.g., &ldquo;A - Follow the squirrel → Part
                                    4&rdquo;) to jump to that story part
                                </Typography>
                                <Typography variant="body2">
                                    • The story branches at decision points where readers make choices that branch the
                                    story into different paths
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* Story Parts */}
            {parts.map((part) => {
                const realDecisionPoints = (part.decisionPoints || []).filter((dp) => !isAutoContinueDecisionPoint(dp));
                return (
                    <Accordion
                        key={part.partNumber}
                        id={`story-part-${part.partNumber}`}
                        className={classes.accordion}
                        expanded={expandedPart === part.partNumber}
                        onChange={handleAccordionChange(part.partNumber)}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionHeader}>
                            <Box display="flex" alignItems="center" width="100%">
                                <span className={classes.partNumber}>{part.partNumber}</span>
                                <Box flexGrow={1}>
                                    <Typography variant="h6">
                                        {part.partLabel || `Story Part ${part.partNumber}`}
                                    </Typography>
                                    <Box display="flex" alignItems="center" mt={0.5} style={{ gap: "8px" }}>
                                        {/* Questions indicator */}
                                        {part.questions && part.questions.length > 0 && (
                                            <Chip
                                                icon={<QuestionAnswerIcon />}
                                                label={`${part.questions.length} Question${
                                                    part.questions.length > 1 ? "s" : ""
                                                }`}
                                                size="small"
                                                variant="outlined"
                                                className={classes.chip}
                                            />
                                        )}
                                        {/* Decision points indicator */}
                                        {realDecisionPoints.length > 0 && (
                                            <Chip
                                                icon={<AccountTreeIcon />}
                                                label={`${realDecisionPoints.length} Decision Point${
                                                    realDecisionPoints.length > 1 ? "s" : ""
                                                }`}
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                                className={classes.chip}
                                            />
                                        )}
                                        {part.isEndNode && (
                                            <Chip
                                                label="End Point"
                                                size="small"
                                                style={{ backgroundColor: "#ff5722", color: "white" }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails style={{ padding: "0px 16px 16px 16px", display: "block" }}>
                            {/* Story Content */}
                            <Box className={classes.contentBox}>
                                <Typography variant="subtitle2" style={{ marginBottom: "8px", fontWeight: "bold" }}>
                                    Story Content
                                </Typography>
                                <Typography variant="body2">{part.content}</Typography>
                            </Box>

                            <Grid container spacing={2}>
                                {/* Decision Points Section */}
                                {realDecisionPoints.length > 0 && (
                                    <Grid item xs={12}>
                                        <Typography variant="h6" className={classes.sectionTitle}>
                                            <AccountTreeIcon />
                                            Decision Points
                                        </Typography>
                                        {realDecisionPoints.map((dp, index) => (
                                            <Card key={index} className={classes.decisionPointCard}>
                                                <CardContent style={{ padding: "12px" }}>
                                                    <Typography variant="subtitle2" style={{ marginBottom: "12px" }}>
                                                        {dp.decisionText}
                                                    </Typography>
                                                    {dp.branches.map((branch, bIndex) => (
                                                        <Box
                                                            key={bIndex}
                                                            className={classes.branchOption}
                                                            onClick={() => handleBranchClick(branch.targetPartNumber)}
                                                            role="button"
                                                            tabIndex={0}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter" || e.key === " ") {
                                                                    e.preventDefault();
                                                                    handleBranchClick(branch.targetPartNumber);
                                                                }
                                                            }}
                                                        >
                                                            <span className={classes.branchLabel}>
                                                                {branch.branchLabel}
                                                            </span>
                                                            <Typography variant="body2" style={{ flex: 1 }}>
                                                                {branch.choiceText}
                                                            </Typography>
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                style={{ gap: "4px" }}
                                                            >
                                                                <ArrowForwardIcon fontSize="small" />
                                                                <Typography variant="caption">
                                                                    Part {branch.targetPartNumber}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Grid>
                                )}

                                {/* Questions Section (for all parts) */}
                                {part.questions && part.questions.length > 0 && (
                                    <Grid item xs={12}>
                                        <Typography variant="h6" className={classes.sectionTitle}>
                                            <QuestionAnswerIcon />
                                            Discussion Questions
                                        </Typography>
                                        {part.questions.map((question, index) => (
                                            <Card key={index} className={classes.questionCard}>
                                                <CardContent style={{ padding: "12px" }}>
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        mb={1}
                                                        style={{ gap: "8px" }}
                                                    >
                                                        <Chip
                                                            label={`Q${question.order}`}
                                                            size="small"
                                                            color="primary"
                                                        />
                                                        <Chip
                                                            label={question.category}
                                                            size="small"
                                                            variant={
                                                                question.category === "Open" ? "filled" : "outlined"
                                                            }
                                                        />
                                                    </Box>
                                                    <Typography variant="body2" style={{ marginBottom: "8px" }}>
                                                        {question.question}
                                                    </Typography>
                                                    {question.keywords && (
                                                        <Typography variant="caption" color="textSecondary">
                                                            Keywords: {question.keywords}
                                                        </Typography>
                                                    )}
                                                    {/* V4 Template enhanced fields */}
                                                    {(question as MappedStoryQuestionType).prompt && (
                                                        <Box mt={1}>
                                                            <Typography
                                                                variant="caption"
                                                                style={{ fontWeight: "bold" }}
                                                            >
                                                                Prompts / Follow-up Questions:
                                                            </Typography>
                                                            {(() => {
                                                                try {
                                                                    const prompts = JSON.parse(
                                                                        (question as MappedStoryQuestionType).prompt ??
                                                                            "",
                                                                    );

                                                                    return Array.isArray(prompts) ? (
                                                                        <Box style={{ paddingLeft: "16px" }}>
                                                                            {prompts.map(
                                                                                (prompt: string, idx: number) => (
                                                                                    <Typography
                                                                                        key={idx}
                                                                                        variant="caption"
                                                                                        display="block"
                                                                                        style={{ marginBottom: "4px" }}
                                                                                    >
                                                                                        • {prompt}
                                                                                    </Typography>
                                                                                ),
                                                                            )}
                                                                        </Box>
                                                                    ) : (
                                                                        <Typography variant="caption" display="block">
                                                                            {
                                                                                (question as MappedStoryQuestionType)
                                                                                    .prompt
                                                                            }
                                                                        </Typography>
                                                                    );
                                                                } catch {
                                                                    return (
                                                                        <Typography variant="caption" display="block">
                                                                            {
                                                                                (question as MappedStoryQuestionType)
                                                                                    .prompt
                                                                            }
                                                                        </Typography>
                                                                    );
                                                                }
                                                            })()}
                                                        </Box>
                                                    )}
                                                    {(question as MappedStoryQuestionType).hint1 && (
                                                        <Box mt={1}>
                                                            <Typography
                                                                variant="caption"
                                                                style={{ fontWeight: "bold" }}
                                                            >
                                                                Help Question (for struggling readers):
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                display="block"
                                                                style={{ fontStyle: "italic" }}
                                                            >
                                                                {(question as MappedStoryQuestionType).hint1}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Grid>
                                )}

                                {/* Empty state for parts without decision points or questions */}
                                {(!part.questions || part.questions.length === 0) &&
                                    realDecisionPoints.length === 0 && (
                                        <Grid item xs={12}>
                                            <Box
                                                p={3}
                                                textAlign="center"
                                                style={{ backgroundColor: "#f5f5f5", borderRadius: "8px" }}
                                            >
                                                <Typography variant="body2" color="textSecondary">
                                                    No additional content (questions or decision points) for this part
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    )}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
};

// Display name for debugging
BranchedStoryAccordionView.displayName = "BranchedStoryAccordionView";
